import { SafeArea } from './safe-area.js';
import { rectHasArea, solveRestoreSeat } from './action-intent-seat.js';
import { uiPx } from './ui-scale.js';

/**
 * Seats the "Resume intent" chip from the live battle furniture.
 *
 * The chip is mounted and seated in ONE task, so its first paint is its final
 * position (docs/asset-loading.md law 2: nothing on screen may settle after it
 * appears). It re-seats only when the viewport changes — never when a hovered
 * hand card grows under it, never when a status ribbon comes or goes — because
 * a control that hops while the pointer is on its way to it cannot be clicked
 * smoothly, and the owner has rejected roaming controls before ("it moves
 * around during a game depending on game state. this is unacceptable").
 *
 * Obstacles are everything a player reads or touches in a battle. The list
 * mirrors the exit control's (audio-settings.js): each of those entries was
 * added after a real collision report, and the chip lives in the same band.
 * The story column is reserved at full height, per the owner's rule that the
 * storyboard squares "must go all the way down".
 */

/** Cosmetic breathing room from the safe-area edges. */
const EDGE_MARGIN = 8;

const OBSTACLE_IDS = [
    'end-turn-btn',
    'skip-tutorial-btn', // doubles as Return to Lobby in a live match
    'turn-timer',
    'mulligan-timer',
    'player-portrait',
    'opponent-portrait',
    'player-health-indicator',
    'player-mana-indicator',
    'player-status-indicator',
    'opponent-health-indicator',
    'opponent-mana-indicator',
    'opponent-status-indicator',
    'player-deck-wrapper',
    'opponent-deck-wrapper',
    'player-deck',
    'opponent-deck',
    'audio-volume-control',
    'audio-settings-panel',
];

const OBSTACLE_SELECTORS = [
    '#player-board .card-slot',
    '#opponent-board .card-slot',
    '#player-board .card',
    '#opponent-board .card',
    '#opponent-hand .card',
    '.deck-count', // pokes above its deck wrapper
    '.summoner-nameplate',
    '.status-indicator',
    /* NOT `.tutorial-tip`. The coach mark is placed by tutorial.js, which lists
       this chip as a blocker and re-solves on the very body mutation that
       mounts it — one frame later. At the instant the chip is seated the mark
       is still wherever it sat while the dialog was up, which on a 4K page
       box is the band above the hand (the dialog is 520px wide there, so the
       mark fits above it). Treating that stale position as an obstacle sent
       the chip beside the hand for good while the mark then moved to the top
       corner. The mark yields to the chip; the chip never yields to the mark. */
    '#story-feed-popup:not([hidden])',
];

/** @param {Element} el */
function painted(el) {
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    // parseFloat, not Number: jsdom reports no computed opacity, and Number('')
    // is 0 — "no information" must not read as "invisible".
    const opacity = Number.parseFloat(style.opacity);
    return !(Number.isFinite(opacity) && opacity === 0);
}

/** @param {Element} el */
function toRect(el) {
    const box = el.getBoundingClientRect();
    return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

function obstacleRects() {
    const rects = [];
    const push = (el) => {
        if (!el || !painted(el)) return;
        const rect = toRect(el);
        if (rectHasArea(rect)) rects.push(rect);
    };
    for (const id of OBSTACLE_IDS) push(document.getElementById(id));
    for (const selector of OBSTACLE_SELECTORS) document.querySelectorAll(selector).forEach(push);
    const feed = document.getElementById('story-feed');
    if (feed && painted(feed)) {
        const rect = toRect(feed);
        if (rectHasArea(rect)) rects.push({ left: rect.left, right: rect.right, top: 0, bottom: window.innerHeight });
    }
    return rects;
}

/** @param {HTMLElement} chip */
export function measureRestoreSeat(chip) {
    const hand = document.getElementById('player-hand');
    /* A card the pointer is on is enlarged and lifted (Escape can minimize
       while one is hovered); anchoring to its grown box would float the chip
       high above the fan once the pointer leaves. Measure the fan at rest. */
    const handCards = hand
        ? Array.from(hand.querySelectorAll('.card:not(.hover-active):not(.dragging)'))
            .filter(painted).map(toRect).filter(rectHasArea)
        : [];
    const handContainer = hand && painted(hand) ? toRect(hand) : null;
    const safe = SafeArea.box(uiPx(EDGE_MARGIN));
    const vmin = Math.min(safe.right - safe.left, safe.bottom - safe.top);
    const gap = Math.round(Math.min(uiPx(14), Math.max(uiPx(8), vmin * 0.0125)));
    return solveRestoreSeat({
        safe,
        chip: { width: chip.offsetWidth, height: chip.offsetHeight },
        hand: handCards,
        handContainer,
        obstacles: obstacleRects(),
        gap,
        bottomClearance: uiPx(18),
    });
}

/** Measure and apply, synchronously. Inline geometry outranks the CSS fallback seat. */
export function seatRestoreChip(chip) {
    const seat = measureRestoreSeat(chip);
    chip.style.left = `${Math.round(seat.left)}px`;
    chip.style.top = `${Math.round(seat.top)}px`;
    chip.style.bottom = 'auto';
    chip.style.transform = 'none';
    chip.dataset.seat = seat.seat;
    chip.dataset.seatClear = seat.clear ? 'true' : 'false';
    return seat;
}

/**
 * Seat now, re-seat on viewport changes only — coalesced to one measurement
 * per frame, so a drag-resize's burst of events costs one layout read, not
 * one per event. Returns the release function; the caller owns the chip's
 * lifetime and must release when it unmounts.
 * @param {HTMLElement} chip
 */
export function holdRestoreSeat(chip) {
    seatRestoreChip(chip);
    let frame = 0;
    const reseat = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
            frame = 0;
            if (!chip.isConnected) return;
            SafeArea.invalidate();
            seatRestoreChip(chip);
        });
    };
    window.addEventListener('resize', reseat, { passive: true });
    window.addEventListener('orientationchange', reseat, { passive: true });
    return () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        window.removeEventListener('resize', reseat);
        window.removeEventListener('orientationchange', reseat);
    };
}
